import {
  APICallState,
  CodeType,
  MFAActions,
  MFAChannel,
  Selectors,
  useWidgetDispatch,
  useWidgetState,
} from '@eCommerce/frontend-legacy-shared-library';
import {useCallback, useEffect, useState} from 'react';
import {useLocation, useSearchParams} from 'react-router-dom';
import {redirectWithForwardUrl} from 'utils/utils';

import CodeVerification from 'components/CodeVerification';
import ErrorPanel from 'components/ErrorPanel';
import PanelContainer from 'components/PanelContainer';
import {useGetForwardUrl} from 'hooks/useGetFowardUrl';
import {useIsOpenWebView} from 'hooks/useIsOpenWebView';
import {useNavigateWithParams} from 'hooks/useNavigateWithParams';
import {AdobeEvent, useTrackEvent} from 'hooks/useTrackEvent';

const StepUpScreen = () => {
  const location = useLocation();
  const profileLoadState = useWidgetState(
    Selectors.mfaProfileStatusSelector,
  ) as APICallState;
  const isVip = useWidgetState(Selectors.isVIPSelector) as boolean;
  const channelPreference = useWidgetState(
    Selectors.mfaChannelPreferenceSelector,
  ) as MFAChannel | undefined;
  const mobilePhone = useWidgetState(Selectors.securityMobilePhoneSelector) as
    | string
    | undefined;
  const email = (useWidgetState(Selectors.securityEmailSelector) ??
    location?.state?.contactInfoEmail) as string | undefined;
  const sendCodeState = useWidgetState(Selectors.sendCodeStateSelector);
  const validateCodeState = useWidgetState(Selectors.validateCodeStateSelector);
  const isCodeInvalid = validateCodeState === APICallState.INVALID_CODE;
  const widgetDispatch = useWidgetDispatch();
  const [retryInProgress, setRetryInProgress] = useState(false);
  const isEnrolling = location?.state?.isEnrolling as boolean | undefined;
  const forwardUrl = useGetForwardUrl() || '';
  const {trackAdobeEvent} = useTrackEvent();
  const noInfo = !(email || mobilePhone);
  const isWebViewAuth = useWidgetState(Selectors.isWebViewAuthSelector);
  const mfaToken = useWidgetState(Selectors.mfaTokenSelector) as
    | string
    | undefined;
  const isOpenWebView = useIsOpenWebView();
  const channel: string = noInfo
    ? 'unknown'
    : channelPreference === MFAChannel.EMAIL
    ? 'email'
    : 'sms';
  const [numberOfCodeAttempts, setNumberOfCodeAttempts] = useState<number>(0);
  const maxCodeAttempts = 3;

  useEffect(() => {
    if ([APICallState.ERROR, APICallState.SUCCESS].includes(profileLoadState)) {
      trackAdobeEvent({
        event: AdobeEvent.click,
        click: {
          name: `mfa stepup ${channel} verification sent`,
        },
      });
    }
  }, [profileLoadState]);

  useEffect(() => {
    if (isCodeInvalid) {
      trackAdobeEvent({
        event: AdobeEvent.click,
        click: {
          name: `mfa stepup ${channel} verification invalid`,
        },
      });
    }
  }, [isCodeInvalid]);

  useEffect(() => {
    widgetDispatch(MFAActions.sendCode());
  }, [widgetDispatch]);

  useEffect(() => {
    if (isEnrolling) {
      trackAdobeEvent({
        event: AdobeEvent.pageLoad,
        page: {
          name: 'mfa opt in success',
        },
      });
    }
  }, []);

  const onCodeEnter = useCallback(
    (code: string) => {
      widgetDispatch(MFAActions.validateCode({code, type: CodeType.OTP}));
      setNumberOfCodeAttempts(
        (numberOfCodeAttempts: number) => numberOfCodeAttempts + 1,
      );
    },
    [widgetDispatch],
  );

  const onResendCode = useCallback(() => {
    widgetDispatch(MFAActions.clearValidateCodeState());
    widgetDispatch(MFAActions.sendCode());
    trackAdobeEvent({
      event: AdobeEvent.click,
      click: {
        name: `mfa stepup ${channel} verification resent`,
      },
    });
    setNumberOfCodeAttempts(0);
  }, [channel, trackAdobeEvent, widgetDispatch]);

  useEffect(() => {
    if (
      sendCodeState === APICallState.SUCCESS ||
      sendCodeState === APICallState.ERROR
    ) {
      setRetryInProgress(false);
    }
  }, [sendCodeState]);

  // FIXME: use the the type name for profile state amd send code state
  if (
    (profileLoadState === APICallState.LOADING ||
      profileLoadState === APICallState.INITIAL) &&
    !retryInProgress
  ) {
    return <PanelContainer isLoading={true} />;
  }

  if (sendCodeState === APICallState.ERROR || retryInProgress) {
    return (
      <ErrorPanel
        onRetryPress={() => {
          setRetryInProgress(true);
          widgetDispatch(MFAActions.sendCode());
        }}
        onCancelPress={undefined}
        retryInProgress={retryInProgress}
      />
    );
  }

  if (validateCodeState === APICallState.SUCCESS) {
    trackAdobeEvent({
      event: AdobeEvent.click,
      click: {
        name: `mfa stepup ${channel} verification success`,
      },
    });
    if (isWebViewAuth) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({value: mfaToken, type: 'mfaToken'}),
      );
    }

    // do not redirect for isopenwebview=true
    if (!isOpenWebView) {
      setTimeout(() => {
        redirectWithForwardUrl(forwardUrl);
      }, 1500);
    } else {
      window.NativeAppWebView.passMfaToken(mfaToken);
      setTimeout(() => {
        window.NativeAppWebView.closeWebView();
      }, 1500);
    }
  }

  return (
    <CodeVerification
      mobilePhone={mobilePhone}
      email={email}
      onCodeEnter={onCodeEnter}
      isCodeInvalid={isCodeInvalid}
      isError={validateCodeState === APICallState.ERROR}
      isLoading={[validateCodeState, sendCodeState].includes(
        APICallState.LOADING,
      )}
      channel={channelPreference}
      isSuccess={validateCodeState === APICallState.SUCCESS}
      onResendCode={onResendCode}
      isVip={isVip}
      isOverMaxCodeAttempts={numberOfCodeAttempts >= maxCodeAttempts}
      isEnrolling={isEnrolling}
    />
  );
};

export default StepUpScreen;
